import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import AddRoleModal from '../../modals/AddRoleModal';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import CompanySubNavigation from '../../../common/CompanySubNavigation';
import RFQWorkflowSettings from './RFQWorkflowSettings';

interface CompanyUserRightsDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

const CompanyUserRightsDashboard: React.FC<CompanyUserRightsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { companyId } = useParams<{ companyId: string }>();
  const { token } = useAuth();
  const [roles, setRoles] = useState<any[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyPlan, setCompanyPlan] = useState<string>('basic'); // 'basic' or 'pro'
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const isMainAdmin = user.role === 'admin'; // Main Gordian admin can edit everything
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Handle add role
  const handleAddRole = () => {
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
  };

  const handleRoleAdded = () => {
    loadRoles();
    setShowAddModal(false);
  };

  // Load company info
  const loadCompanyInfo = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setCompanyName(data.data.name || 'Company');
          setCompanyPlan(data.data.plan || 'basic'); // Get company plan
        }
      }
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  // Check if a field is locked based on plan and user type
  const isFieldLocked = (field: string, roleName: string) => {
    // Main admin can edit everything
    if (isMainAdmin) return false;

    // Locked fields for both BASIC and PRO plans (for non-main-admin users)
    const lockedFields = ['process_rfq', 'automated_mails', 'brave_search'];

    if (lockedFields.includes(field)) {
      // For both BASIC and PRO plans, these fields are locked for all roles (non-main-admin users)
      return true;
    }

    return false;
  };

  // Get tooltip message for locked fields
  const getLockedFieldTooltip = (field: string) => {
    if (isMainAdmin) return '';

    if (companyPlan === 'basic') {
      return 'This feature requires upgrading to PRO plan';
    } else {
      return 'This feature cannot be changed';
    }
  };

  // Load roles data
  const loadRoles = async () => {
    setIsLoading(true);
    try {
      // Load company info first to get plan
      await loadCompanyInfo();

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load roles');
      }

      const data = await response.json();

      if (data.success) {
        // Transform roles for DataDashboard
        const transformedRoles = (data.data || []).map((role: any) => {
          // Process RFQ: convert boolean to 'manual' or 'auto ai'
          let processRfqValue = role.permissions?.process_rfq === true ? 'auto ai' : 'manual';
          let automatedMailsValue = role.permissions?.automated_mails ? 'Yes' : 'No';
          let braveSearchValue = role.permissions?.brave_search ? 'Yes' : 'No';

          return {
            id: role._id,
            role_id: role.role_id,
            name: role.name,
            open_rfq_request: role.permissions?.open_rfq_request ? 'Yes' : 'No',
            rfq_operations: role.permissions?.rfq_operations ? 'Yes' : 'No',
            edit_supplier: role.permissions?.edit_supplier ? 'Yes' : 'No',
            edit_product: role.permissions?.edit_product ? 'Yes' : 'No',
            approve_ability: role.permissions?.approve_ability ? 'Yes' : 'No',
            see_all_requests: role.permissions?.see_all_requests ? 'Yes' : 'No',
            user_rights: role.permissions?.user_rights ? 'Yes' : 'No',
            edit_address: role.permissions?.edit_address ? 'Yes' : 'No',
            control_ability: role.permissions?.control_ability ? 'Yes' : 'No',
            // Process RFQ: convert boolean to 'manual' or 'auto ai'
            process_rfq: processRfqValue,
            brave_search: braveSearchValue,
            automated_mails: automatedMailsValue,
            is_default: role.is_default,
            _permissions: role.permissions // Keep original for editing
          };
        });

        setRoles(transformedRoles);
        setPaginationInfo({
          page: 1,
          limit: 25,
          total: transformedRoles.length,
          pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error: any) {
      console.error('Error loading roles:', error);
      setError(error.message || 'Failed to load roles');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  // Handle edit role
  const handleEditRole = async (id: string, field: string, value: any) => {
    try {
      const role = roles.find(r => r.id === id);
      if (!role) return;

      // Check if field is locked (for non-main-admin users)
      if (!isMainAdmin && isFieldLocked(field, role.name)) {
        // Show alert for BASIC plan upgrade message
        if (companyPlan === 'basic') {
          alert('This feature requires upgrading to PRO plan. Please contact your administrator.');
        } else {
          alert('This feature cannot be changed.');
        }
        return;
      }

      // Permission fields that toggle on click
      // Permission fields that toggle on click
      const permissionFields = ['open_rfq_request', 'rfq_operations', 'edit_supplier', 'edit_product', 'approve_ability', 'see_all_requests', 'user_rights', 'edit_address', 'control_ability', 'brave_search', 'automated_mails'];

      // Process RFQ has special handling: 'manual' ↔ 'auto ai'
      if (field === 'process_rfq') {
        const currentValue = role[field];
        const newValue = currentValue === 'manual' ? 'auto ai' : 'manual';
        const newBoolValue = newValue === 'auto ai';

        // Update permissions object
        const updatedPermissions = {
          ...role._permissions,
          process_rfq: newBoolValue
        };

        // Update backend
        const response = await fetch(`${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles/${id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ permissions: updatedPermissions })
        });

        if (!response.ok) {
          throw new Error('Failed to update role');
        }

        // Update local state
        setRoles(roles.map(r =>
          r.id === id
            ? { ...r, process_rfq: newValue, _permissions: updatedPermissions }
            : r
        ));
        return;
      }

      if (permissionFields.includes(field)) {
        // Toggle permission: Yes → No, No → Yes
        const currentValue = role[field];
        const newValue = currentValue === 'Yes' ? 'No' : 'Yes';
        const newBoolValue = newValue === 'Yes';

        // Update permissions object
        const updatedPermissions = {
          ...role._permissions,
          [field]: newBoolValue
        };

        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles/${id}`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ permissions: updatedPermissions })
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update role');
        }

        // Update local state
        const updatedRole = {
          ...role,
          [field]: newValue,
          _permissions: updatedPermissions
        };
        setRoles(roles.map(r => r.id === id ? updatedRole : r));
      } else if (field === 'name') {
        // Update role name
        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles/${id}`,
          {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ name: value })
          }
        );

        if (!response.ok) {
          throw new Error('Failed to update role name');
        }

        setRoles(roles.map(r => r.id === id ? { ...r, name: value } : r));
      }
    } catch (error) {
      console.error('Error editing role:', error);
    }
  };

  // Handle delete roles
  const handleDeleteRoles = async (ids: string[]) => {
    try {
      for (const id of ids) {
        const role = roles.find(r => r.id === id);
        if (role?.is_default) {
          alert('Cannot delete default roles');
          continue;
        }

        const response = await fetch(
          `${API_ENDPOINTS.BASE_URL}/v2/companies/${companyId}/roles/${id}`,
          {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to delete role');
        }
      }

      // Reload roles
      loadRoles();
    } catch (error) {
      console.error('Error deleting roles:', error);
    }
  };

  // Handle data fetch
  const handleDataFetch = (params: any) => {
    loadRoles();
  };

  // Handle export
  const handleExport = async (exportData: any) => {
    try {
      // Simple CSV export for roles
      const headers = ['Role ID', 'Role Name', 'Open RFQ', 'Edit Supplier', 'Edit Product', 'Approve', 'See All', 'User Rights', 'Edit Address', 'Process RFQ', 'Brave Search', 'Automated Mails'];
      const rows = roles.map(role => [
        role.role_id,
        role.name,
        role.open_rfq_request,
        role.rfq_operations,
        role.edit_supplier,
        role.edit_product,
        role.approve_ability,
        role.see_all_requests,
        role.user_rights,
        role.edit_address,
        role.process_rfq,
        role.brave_search,
        role.automated_mails
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      // Format: isim_YYYYMMDD
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      a.download = `${companyName}_roles_${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting roles:', error);
    }
  };

  // Load initial data
  useEffect(() => {
    if (companyId) {
      loadCompanyInfo();
      loadRoles();
    }
  }, [companyId]);

  // Role columns with permissions as clickable toggles
  const roleColumns: any[] = [
    { key: 'role_id', title: 'Role ID', type: 'text' as const, sortable: true, editable: false, width: '100px' },
    { key: 'name', title: 'Role Name', type: 'text' as const, sortable: true, editable: true, width: '200px' },
    {
      key: 'open_rfq_request',
      title: 'Open RFQ Request',
      type: 'text' as const,
      sortable: true,
      editable: false,  // Not editable via input - only click to toggle
      width: '150px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'open_rfq_request', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'rfq_operations',
      title: 'RFQ Operations',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'rfq_operations', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'edit_supplier',
      title: 'Edit Supplier',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'edit_supplier', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'edit_product',
      title: 'Edit Product',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'edit_product', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'approve_ability',
      title: 'Approve Ability',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'approve_ability', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'see_all_requests',
      title: 'See All Requests',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'see_all_requests', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'user_rights',
      title: 'User Rights',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'user_rights', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'edit_address',
      title: 'Edit Address',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'edit_address', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'process_rfq',
      title: 'Process RFQ',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => {
        const isLocked = !isMainAdmin && isFieldLocked('process_rfq', row.name);
        const tooltip = getLockedFieldTooltip('process_rfq');
        const displayValue = value === 'auto ai' ? 'Auto AI' : 'Manual';
        // Using color palette from image: Blue tones (#2171b5, #6baed6, #deebf7) for Auto AI, Gray tones (#525252, #bdbdbd, #f0f0f0) for Manual
        const textColor = value === 'auto ai' ? '#2171b5' : '#525252'; // Dark Blue (#2171b5) for Auto AI, Dark Gray (#525252) for Manual
        const bgColor = value === 'auto ai' ? '#deebf7' : '#f0f0f0'; // Light Blue (#deebf7) for Auto AI, Light Gray (#f0f0f0) for Manual
        const borderColor = value === 'auto ai' ? '#6baed6' : '#bdbdbd'; // Medium Blue (#6baed6) for Auto AI, Medium Gray (#bdbdbd) for Manual
        const tooltipId = `tooltip-process-rfq-${row.id}`;

        return (
          <span
            onClick={() => isEditing && !isLocked && handleEditRole(row.id, 'process_rfq', value)}
            className={isLocked ? 'locked-field-tooltip' : ''}
            data-tooltip-id={isLocked ? tooltipId : undefined}
            onMouseEnter={(e) => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltipEl.style.display = 'block';
                  const leftPos = rect.left + rect.width / 2;
                  // Calculate top position - ensure it's well above the element
                  const topPos = rect.top - 12;
                  tooltipEl.style.left = `${leftPos}px`;
                  tooltipEl.style.top = `${topPos}px`;
                  tooltipEl.style.transform = 'translate(-50%, -100%)';
                  // Ensure tooltip doesn't go off screen
                  setTimeout(() => {
                    const tooltipRect = tooltipEl.getBoundingClientRect();
                    if (tooltipRect.left < 10) {
                      tooltipEl.style.left = `${leftPos - tooltipRect.left + 10}px`;
                      tooltipEl.style.transform = 'translate(-10px, -100%)';
                    } else if (tooltipRect.right > window.innerWidth - 10) {
                      tooltipEl.style.left = `${leftPos - (tooltipRect.right - window.innerWidth + 10)}px`;
                      tooltipEl.style.transform = 'translate(calc(-100% + 10px), -100%)';
                    }
                    // If tooltip is too close to top, move it below
                    if (tooltipRect.top < 20) {
                      tooltipEl.style.top = `${rect.bottom + 12}px`;
                      tooltipEl.style.transform = tooltipEl.style.transform.replace('-100%', '0%');
                      // Update arrow position
                      const arrow = tooltipEl.querySelector('.tooltip-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.style.top = '-6px';
                        arrow.style.borderTopColor = 'transparent';
                        arrow.style.borderBottomColor = '#2171b5';
                      }
                    }
                  }, 0);
                }
              }
            }}
            onMouseLeave={() => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  tooltipEl.style.display = 'none';
                }
              }
            }}
            style={{
              fontSize: '13px',
              fontWeight: '600',
              color: isLocked ? (value === 'auto ai' ? '#2171b5' : '#9ca3af') : textColor, // Auto AI always blue, even when locked
              cursor: isEditing && !isLocked ? 'pointer' : isLocked ? 'not-allowed' : 'default',
              userSelect: 'none',
              display: 'inline-block',
              padding: isEditing ? '6px 14px' : '4px 8px',
              border: isEditing ? `1px solid ${isLocked ? '#d1d5db' : borderColor}` : 'none',
              borderRadius: isEditing ? '6px' : '4px',
              backgroundColor: isEditing && !isLocked ? bgColor : 'transparent', // No background when locked
              opacity: isLocked && value === 'manual' ? 0.6 : 1, // Only reduce opacity for locked Manual
              transition: 'all 0.2s',
              textTransform: 'none',
              letterSpacing: '0.3px',
              position: 'relative'
            }}
          >
            {displayValue}
            {isLocked && (
              <span
                id={tooltipId}
                className="tooltip-text-fixed"
                style={{
                  display: 'none',
                  position: 'fixed',
                  padding: '8px 12px',
                  backgroundColor: '#2171b5',
                  color: '#fff',
                  fontSize: '12px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  zIndex: 99999,
                  boxShadow: '0 4px 12px rgba(33, 113, 181, 0.3)',
                  fontWeight: '500',
                  pointerEvents: 'none'
                }}
              >
                {tooltip}
                <span
                  className="tooltip-arrow"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: '6px solid transparent',
                    borderTopColor: '#2171b5'
                  }}
                />
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'brave_search',
      title: 'Brave Search',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => {
        const isLocked = !isMainAdmin && isFieldLocked('brave_search', row.name);
        const tooltip = getLockedFieldTooltip('brave_search');
        const tooltipId = `tooltip-brave-search-${row.id}`;
        // Use blue color palette for locked Yes values
        const isYes = value === 'Yes';
        const lockedColor = isLocked && isYes ? '#2171b5' : isLocked ? '#9ca3af' : '#000';
        const lockedBg = isLocked && isYes ? '#deebf7' : isLocked ? '#f0f0f0' : 'transparent';

        return (
          <span
            onClick={() => isEditing && !isLocked && handleEditRole(row.id, 'brave_search', value)}
            data-tooltip-id={isLocked ? tooltipId : undefined}
            onMouseEnter={(e) => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltipEl.style.display = 'block';
                  const leftPos = rect.left + rect.width / 2;
                  // Calculate top position - ensure it's well above the element
                  const topPos = rect.top - 12;
                  tooltipEl.style.left = `${leftPos}px`;
                  tooltipEl.style.top = `${topPos}px`;
                  tooltipEl.style.transform = 'translate(-50%, -100%)';
                  // Ensure tooltip doesn't go off screen
                  setTimeout(() => {
                    const tooltipRect = tooltipEl.getBoundingClientRect();
                    if (tooltipRect.left < 10) {
                      tooltipEl.style.left = `${leftPos - tooltipRect.left + 10}px`;
                      tooltipEl.style.transform = 'translate(-10px, -100%)';
                    } else if (tooltipRect.right > window.innerWidth - 10) {
                      tooltipEl.style.left = `${leftPos - (tooltipRect.right - window.innerWidth + 10)}px`;
                      tooltipEl.style.transform = 'translate(calc(-100% + 10px), -100%)';
                    }
                    // If tooltip is too close to top, move it below
                    if (tooltipRect.top < 20) {
                      tooltipEl.style.top = `${rect.bottom + 12}px`;
                      tooltipEl.style.transform = tooltipEl.style.transform.replace('-100%', '0%');
                      // Update arrow position
                      const arrow = tooltipEl.querySelector('.tooltip-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.style.top = '-6px';
                        arrow.style.borderTopColor = 'transparent';
                        arrow.style.borderBottomColor = '#2171b5';
                      }
                    }
                  }, 0);
                }
              }
            }}
            onMouseLeave={() => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  tooltipEl.style.display = 'none';
                }
              }
            }}
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: lockedColor,
              cursor: isEditing && !isLocked ? 'pointer' : isLocked ? 'not-allowed' : 'default',
              userSelect: 'none',
              display: 'inline-block',
              padding: isEditing ? '4px 12px' : '0',
              border: isEditing ? `1px solid ${isLocked && isYes ? '#6baed6' : '#d1d5db'}` : 'none',
              borderRadius: isEditing ? '4px' : '0',
              backgroundColor: 'transparent', // No background for ticks
              opacity: isLocked && !isYes ? 0.6 : 1,
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            {value === 'Yes' ? '✓' : '✗'}
            {isLocked && (
              <span
                id={tooltipId}
                className="tooltip-text-fixed"
                style={{
                  display: 'none',
                  position: 'fixed',
                  padding: '8px 12px',
                  backgroundColor: '#2171b5',
                  color: '#fff',
                  fontSize: '12px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  zIndex: 99999,
                  boxShadow: '0 4px 12px rgba(33, 113, 181, 0.3)',
                  fontWeight: '500',
                  pointerEvents: 'none'
                }}
              >
                {tooltip}
                <span
                  className="tooltip-arrow"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: '6px solid transparent',
                    borderTopColor: '#2171b5'
                  }}
                />
              </span>
            )}
          </span>
        );
      }
    },
    {
      key: 'control_ability',
      title: 'Control Ability',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '130px',
      render: (value: any, row: any) => (
        <span
          onClick={() => isEditing && handleEditRole(row.id, 'control_ability', value)}
          style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#000',
            cursor: isEditing ? 'pointer' : 'default',
            userSelect: 'none',
            display: 'inline-block',
            padding: isEditing ? '4px 12px' : '0',
            border: isEditing ? '1px solid #d1d5db' : 'none',
            borderRadius: isEditing ? '4px' : '0',
            backgroundColor: isEditing ? '#f9fafb' : 'transparent',
            transition: 'all 0.2s'
          }}
        >
          {value === 'Yes' ? '✓' : '✗'}
        </span>
      )
    },
    {
      key: 'automated_mails',
      title: 'Automated Mails',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: any, row: any) => {
        const isLocked = !isMainAdmin && isFieldLocked('automated_mails', row.name);
        const tooltip = getLockedFieldTooltip('automated_mails');
        const tooltipId = `tooltip-automated-mails-${row.id}`;
        // Use blue color palette for locked Yes values
        const isYes = value === 'Yes';
        const lockedColor = isLocked && isYes ? '#2171b5' : isLocked ? '#9ca3af' : '#000';
        const lockedBg = isLocked && isYes ? '#deebf7' : isLocked ? '#f0f0f0' : 'transparent';

        return (
          <span
            onClick={() => isEditing && !isLocked && handleEditRole(row.id, 'automated_mails', value)}
            data-tooltip-id={isLocked ? tooltipId : undefined}
            onMouseEnter={(e) => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  tooltipEl.style.display = 'block';
                  const leftPos = rect.left + rect.width / 2;
                  // Calculate top position - ensure it's well above the element
                  const topPos = rect.top - 12;
                  tooltipEl.style.left = `${leftPos}px`;
                  tooltipEl.style.top = `${topPos}px`;
                  tooltipEl.style.transform = 'translate(-50%, -100%)';
                  // Ensure tooltip doesn't go off screen
                  setTimeout(() => {
                    const tooltipRect = tooltipEl.getBoundingClientRect();
                    if (tooltipRect.left < 10) {
                      tooltipEl.style.left = `${leftPos - tooltipRect.left + 10}px`;
                      tooltipEl.style.transform = 'translate(-10px, -100%)';
                    } else if (tooltipRect.right > window.innerWidth - 10) {
                      tooltipEl.style.left = `${leftPos - (tooltipRect.right - window.innerWidth + 10)}px`;
                      tooltipEl.style.transform = 'translate(calc(-100% + 10px), -100%)';
                    }
                    // If tooltip is too close to top, move it below
                    if (tooltipRect.top < 20) {
                      tooltipEl.style.top = `${rect.bottom + 12}px`;
                      tooltipEl.style.transform = tooltipEl.style.transform.replace('-100%', '0%');
                      // Update arrow position
                      const arrow = tooltipEl.querySelector('.tooltip-arrow') as HTMLElement;
                      if (arrow) {
                        arrow.style.top = '-6px';
                        arrow.style.borderTopColor = 'transparent';
                        arrow.style.borderBottomColor = '#2171b5';
                      }
                    }
                  }, 0);
                }
              }
            }}
            onMouseLeave={() => {
              if (isLocked) {
                const tooltipEl = document.getElementById(tooltipId);
                if (tooltipEl) {
                  tooltipEl.style.display = 'none';
                }
              }
            }}
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: lockedColor,
              cursor: isEditing && !isLocked ? 'pointer' : isLocked ? 'not-allowed' : 'default',
              userSelect: 'none',
              display: 'inline-block',
              padding: isEditing ? '4px 12px' : '0',
              border: isEditing ? `1px solid ${isLocked && isYes ? '#6baed6' : '#d1d5db'}` : 'none',
              borderRadius: isEditing ? '4px' : '0',
              backgroundColor: 'transparent', // No background for ticks
              opacity: isLocked && !isYes ? 0.6 : 1,
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            {value === 'Yes' ? '✓' : '✗'}
            {isLocked && (
              <span
                id={tooltipId}
                className="tooltip-text-fixed"
                style={{
                  display: 'none',
                  position: 'fixed',
                  padding: '8px 12px',
                  backgroundColor: '#2171b5',
                  color: '#fff',
                  fontSize: '12px',
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  zIndex: 99999,
                  boxShadow: '0 4px 12px rgba(33, 113, 181, 0.3)',
                  fontWeight: '500',
                  pointerEvents: 'none'
                }}
              >
                {tooltip}
                <span
                  className="tooltip-arrow"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    border: '6px solid transparent',
                    borderTopColor: '#2171b5'
                  }}
                />
              </span>
            )}
          </span>
        );
      }
    }
  ];

  // Only show secondary sidebar for admin users, not for regular users
  const isAdmin = user.role === 'admin';

  return (
    <>
      <style>{`
        .tooltip-text-fixed {
          transition: opacity 0.2s ease-in-out;
        }
      `}</style>
      <div className="admin-layout-v2 with-company-navigation">
        <DataDashboard
          title={`User Roles - ${companyName}`}
          collection="company_roles"
          pageDescription="Configure permissions and user roles"
          data={roles}
          columns={roleColumns}
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          onDataChange={setRoles}
          onDataFetch={handleDataFetch}
          onEdit={handleEditRole}
          onEditToggle={handleEditToggle}
          isEditing={isEditing}
          onDelete={handleDeleteRoles}
          onExport={handleExport}
          paginationInfo={paginationInfo}
          showAddButton={true}
          addButtonLabel="Add New Role"
          onAdd={handleAddRole}
          showSearch={false}
          showSelection={!isEditing}
          showEditButton={true}
          showDeleteButton={false}
          layoutType={isAdmin ? 'admin' : 'user'}
          getDynamicSubItems={getDynamicSubItems}
          navigationActions={
            isAdmin ? (
              <CompanySubNavigation
                currentRoute={currentRoute}
                onNavigate={onNavigate}
                isAdmin={isAdmin}
              />
            ) : undefined
          }
        />

        {/* RFQ Workflow Settings */}
        {companyId && token && (
          <RFQWorkflowSettings companyId={companyId} token={token} />
        )}

        {/* Add Role Modal */}
        <AddRoleModal
          isOpen={showAddModal}
          onClose={handleCloseAddModal}
          onSuccess={handleRoleAdded}
          companyId={companyId || ''}
        />
      </div>
    </>
  );
};

export default CompanyUserRightsDashboard;
